import { useState, useCallback } from 'react';
import type { CellCoordinate } from './types';

/**
 * Keyboard Navigation Hook for Spreadsheet
 *
 * Manages focus state and keyboard navigation (Arrow keys, Tab, Home, End)
 */

interface UseSpreadsheetNavigationOptions {
  totalRows: number;
  totalColumns: number;
  onCellFocus?: (coordinate: CellCoordinate) => void;
}

export function useSpreadsheetNavigation({
  totalRows,
  totalColumns,
  onCellFocus
}: UseSpreadsheetNavigationOptions) {
  const [focusedCell, setFocusedCell] = useState<CellCoordinate | null>(null);

  /**
   * Navigate to specific cell
   */
  const navigateToCell = useCallback(
    (coordinate: CellCoordinate) => {
      // Validate coordinates
      if (
        coordinate.rowIndex < 0 ||
        coordinate.rowIndex >= totalRows ||
        coordinate.columnIndex < 0 ||
        coordinate.columnIndex >= totalColumns
      ) {
        return;
      }

      setFocusedCell(coordinate);
      onCellFocus?.(coordinate);
    },
    [totalRows, totalColumns, onCellFocus]
  );

  /**
   * Navigate relative to current cell.
   * Arrow key navigation passes autoEdit=false; Tab/Enter navigation passes autoEdit=true.
   */
  const navigate = useCallback(
    (from: CellCoordinate, direction: 'up' | 'down' | 'left' | 'right' | 'tab' | 'shift-tab' | 'self', autoEdit?: boolean) => {
      let newRow = from.rowIndex;
      let newCol = from.columnIndex;

      switch (direction) {
        case 'up':
          newRow = Math.max(0, from.rowIndex - 1);
          break;
        case 'down':
          newRow = Math.min(totalRows - 1, from.rowIndex + 1);
          break;
        case 'left':
          newCol = Math.max(0, from.columnIndex - 1);
          break;
        case 'right':
          newCol = Math.min(totalColumns - 1, from.columnIndex + 1);
          break;
        case 'tab': {
          // Tab: move right, wrap to first column of next row at end of row
          if (from.columnIndex === totalColumns - 1) {
            newRow = Math.min(totalRows - 1, from.rowIndex + 1);
            newCol = 0;
          } else {
            newCol = from.columnIndex + 1;
          }
          break;
        }
        case 'shift-tab': {
          // Shift+Tab: move left, wrap to last column of previous row at start of row
          if (from.columnIndex === 0) {
            newRow = Math.max(0, from.rowIndex - 1);
            newCol = totalColumns - 1;
          } else {
            newCol = from.columnIndex - 1;
          }
          break;
        }
        case 'self':
          // Focus the cell itself without moving (used for click-to-focus)
          break;
      }

      navigateToCell({ rowIndex: newRow, columnIndex: newCol, autoEdit: autoEdit ?? false });
    },
    [totalRows, totalColumns, navigateToCell]
  );

  /**
   * Navigate on Enter (save + move down, autoEdit = true)
   */
  const navigateOnEnter = useCallback(
    (from: CellCoordinate) => {
      navigate(from, 'down', true);
    },
    [navigate]
  );

  /**
   * Navigate on Tab (save + move right, wrap to next row, autoEdit = true)
   */
  const navigateOnTab = useCallback(
    (from: CellCoordinate) => {
      const isLastColumn = from.columnIndex === totalColumns - 1;

      if (isLastColumn) {
        // Wrap to first column of next row
        const nextRow = Math.min(totalRows - 1, from.rowIndex + 1);
        navigateToCell({ rowIndex: nextRow, columnIndex: 0, autoEdit: true });
      } else {
        navigate(from, 'right', true);
      }
    },
    [totalColumns, totalRows, navigate, navigateToCell]
  );

  /**
   * Navigate to first column (Home key)
   */
  const navigateToFirstColumn = useCallback(
    (from: CellCoordinate) => {
      navigateToCell({ rowIndex: from.rowIndex, columnIndex: 0 });
    },
    [navigateToCell]
  );

  /**
   * Navigate to last column (End key)
   */
  const navigateToLastColumn = useCallback(
    (from: CellCoordinate) => {
      navigateToCell({ rowIndex: from.rowIndex, columnIndex: totalColumns - 1 });
    },
    [totalColumns, navigateToCell]
  );

  /**
   * Navigate to top-left cell (Ctrl+Home)
   */
  const navigateToStart = useCallback(() => {
    navigateToCell({ rowIndex: 0, columnIndex: 0 });
  }, [navigateToCell]);

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent, currentCell: CellCoordinate) => {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          navigate(currentCell, 'up');
          break;
        case 'ArrowDown':
          event.preventDefault();
          navigate(currentCell, 'down');
          break;
        case 'ArrowLeft':
          event.preventDefault();
          navigate(currentCell, 'left');
          break;
        case 'ArrowRight':
          event.preventDefault();
          navigate(currentCell, 'right');
          break;
        case 'Home':
          event.preventDefault();
          if (event.ctrlKey) {
            navigateToStart();
          } else {
            navigateToFirstColumn(currentCell);
          }
          break;
        case 'End':
          event.preventDefault();
          navigateToLastColumn(currentCell);
          break;
      }
    },
    [navigate, navigateToStart, navigateToFirstColumn, navigateToLastColumn]
  );

  return {
    focusedCell,
    setFocusedCell,
    navigateToCell,
    navigate,
    navigateOnEnter,
    navigateOnTab,
    navigateToFirstColumn,
    navigateToLastColumn,
    navigateToStart,
    handleKeyDown
  };
}
